// /account — your profile + sessions + change password.

import { ApiError, authFetch } from '@/lib/api';
import { dateOnly, relTime } from '@/lib/format';
import { ObjectHeader, Pill } from '@/primitives';
import { useAuth } from '@/state/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/_authed/account')({
  component: AccountPage,
});

interface SessionRow {
  id: string;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
  last_used_at: string | null;
  expires_at: string;
  current: boolean;
}

function AccountPage() {
  const me = useAuth((s) => s.me);
  const qc = useQueryClient();
  const [changing, setChanging] = useState(false);
  const [pwOld, setPwOld] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwErr, setPwErr] = useState<string | null>(null);
  const [pwOk, setPwOk] = useState(false);

  const sessions = useQuery({
    queryKey: ['auth', 'sessions'],
    queryFn: () => authFetch<{ sessions: SessionRow[] }>('/v1/auth/sessions'),
  });

  async function revoke(id: string) {
    await authFetch(`/v1/auth/sessions/${id}/revoke`, { method: 'POST' }).catch(() => {});
    void qc.invalidateQueries({ queryKey: ['auth', 'sessions'] });
  }

  async function changePassword() {
    setPwErr(null);
    setPwOk(false);
    if (pwNew.length < 14) {
      setPwErr('weak_password');
      return;
    }
    setChanging(true);
    try {
      await authFetch('/v1/auth/password/change', {
        method: 'POST',
        body: { current_password: pwOld, new_password: pwNew },
      });
      setPwOld('');
      setPwNew('');
      setPwOk(true);
      // password change revokes all sessions; force a refresh probe
      void qc.invalidateQueries({ queryKey: ['auth', 'sessions'] });
    } catch (e) {
      setPwErr(e instanceof ApiError ? e.code : 'internal_error');
    } finally {
      setChanging(false);
    }
  }

  return (
    <div
      style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', minHeight: 0 }}
    >
      <ObjectHeader
        type="CASE"
        id="ACCOUNT"
        title={me?.display_name ?? me?.email ?? 'Account'}
        sub={`${me?.role.replace('_', ' ').toUpperCase() ?? ''} · ${me?.tenant_name ?? ''}`}
        pills={[
          { tone: 'green', label: 'MFA ENABLED' },
          { tone: 'primary', label: me?.classification ?? 'PUBLIC' },
        ]}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: 16 }}>
        <div className="pane">
          <div className="pane-h">
            <span>PROFILE</span>
          </div>
          <div className="pane-body">
            <div className="pg">
              <div className="pg-row">
                <div className="k">EMAIL</div>
                <div className="v">{me?.email ?? '—'}</div>
              </div>
              <div className="pg-row">
                <div className="k">ROLE</div>
                <div className="v">{me?.role}</div>
              </div>
              <div className="pg-row">
                <div className="k">TENANT</div>
                <div className="v">{me?.tenant_name}</div>
              </div>
              <div className="pg-row">
                <div className="k">TENANT ID</div>
                <div className="v">{me?.tenant_id}</div>
              </div>
              <div className="pg-row">
                <div className="k">USER ID</div>
                <div className="v">{me?.user_id}</div>
              </div>
              <div className="pg-row">
                <div className="k">CLEARANCE</div>
                <div className="v">{me?.classification}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="pane">
          <div className="pane-h">
            <span>CHANGE PASSWORD</span>
          </div>
          <div
            className="pane-body"
            style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            {pwErr && (
              <div
                style={{
                  padding: '6px 10px',
                  border: '1px solid var(--red-line)',
                  background: 'var(--red-soft)',
                  color: 'var(--red)',
                  fontFamily: 'var(--mono)',
                  fontSize: 11,
                }}
              >
                {pwErr}
              </div>
            )}
            {pwOk && (
              <div
                style={{
                  padding: '6px 10px',
                  border: '1px solid var(--green-line)',
                  background: 'var(--green-soft)',
                  color: 'var(--green)',
                  fontFamily: 'var(--mono)',
                  fontSize: 11,
                }}
              >
                password updated · all other sessions revoked
              </div>
            )}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="lbl">CURRENT PASSWORD</span>
              <input
                type="password"
                value={pwOld}
                onChange={(e) => setPwOld(e.target.value)}
                style={{
                  height: 28,
                  padding: '0 8px',
                  background: 'var(--bg-base)',
                  border: '1px solid var(--bd-strong)',
                  color: 'var(--fg-1)',
                  fontFamily: 'var(--mono)',
                  fontSize: 11,
                }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span className="lbl">
                NEW PASSWORD <span className="dim2">(≥14 chars)</span>
              </span>
              <input
                type="password"
                value={pwNew}
                onChange={(e) => setPwNew(e.target.value)}
                style={{
                  height: 28,
                  padding: '0 8px',
                  background: 'var(--bg-base)',
                  border: '1px solid var(--bd-strong)',
                  color: 'var(--fg-1)',
                  fontFamily: 'var(--mono)',
                  fontSize: 11,
                }}
              />
            </label>
            <button
              type="button"
              className="btn pri"
              disabled={changing || !pwOld || pwNew.length < 14}
              onClick={changePassword}
              style={{ height: 28, justifyContent: 'center' }}
            >
              {changing ? 'UPDATING…' : 'UPDATE PASSWORD'}
            </button>
          </div>
        </div>
      </div>

      <div className="pane" style={{ margin: '0 16px 16px' }}>
        <div className="pane-h">
          <span>ACTIVE SESSIONS</span>
          <span className="meta mono">{sessions.data?.sessions.length ?? 0}</span>
        </div>
        <div className="pane-body">
          {sessions.isLoading ? (
            <div className="meta mono" style={{ padding: 12 }}>
              loading…
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>STARTED</th>
                  <th>LAST USED</th>
                  <th>IP</th>
                  <th>USER AGENT</th>
                  <th>EXPIRES</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sessions.data?.sessions.map((s) => (
                  <tr key={s.id}>
                    <td className="mn dim">{relTime(s.created_at)}</td>
                    <td className="mn dim">{relTime(s.last_used_at) ?? '—'}</td>
                    <td className="mn">{s.ip ?? '—'}</td>
                    <td
                      className="dim"
                      style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {s.user_agent ?? '—'}
                    </td>
                    <td className="mn dim">{dateOnly(s.expires_at)}</td>
                    <td className="r">
                      {s.current ? (
                        <Pill tone="primary" dot={false}>
                          THIS SESSION
                        </Pill>
                      ) : (
                        <button type="button" className="btn xs" onClick={() => revoke(s.id)}>
                          REVOKE
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
